import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Check, ChevronDown, ExternalLink } from 'lucide-react';
import { Fragment } from 'react';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { PluginPanel } from '@/features/plugins/plugin-workbench';
import { dockApp } from '@/lib/desktop-shell/app-panels';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { PluginFrame, type PluginFrameProps } from './plugin-frame';
import {
  dockedBridgeKey,
  groupedSections,
  remoteAppBrowserUrl,
  remoteFrameActions,
  remoteItemPath,
  sendToRemoteApp,
  useRemoteApp,
} from './remote-app-bridge';
import { RemoteAppPanel } from './remote-app-panel';

/**
 * A Crowlingo application shown in Crowdex (brand/crow-extensions.html, « Apps »): its web app in
 * the host's frame, its own top bar and column hidden. The bar shows the page's title and
 * primary action the app sends through the embed bridge, ↗ opens it full screen in the browser
 * where it is; its sections are in the host's left column full screen (`AppHomeColumn`), in a
 * picker after the title docked.
 */
type RemoteFrameProps = Pick<
  PluginFrameProps,
  'onClose' | 'openElsewhere' | 'onBack' | 'backLabel'
>;

/** ↗: the app full screen in the user's browser, where it is now. */
function OpenInBrowser({ bridgeKey, title }: { bridgeKey: string; title: string }) {
  const remote = useRemoteApp(bridgeKey);
  const url = remoteAppBrowserUrl(remote.origin, remote.nav?.path);
  if (url === null) return null;
  return (
    <Button asChild size="icon-sm" title={`Open ${title} in the browser`} variant="ghost">
      <a
        aria-label={`Open ${title} in the browser`}
        data-remote-open-browser
        href={url}
        rel="noopener noreferrer"
        target="_blank"
      >
        <ExternalLink className="size-4" />
      </a>
    </Button>
  );
}

/** The app full screen (`/apps/<plugin>[/<route>]`): its route is the deep link it opens. */
export function RemoteAppFullScreen({
  panel,
  bridgeKey,
  route,
  ...frame
}: RemoteFrameProps & { panel: PluginPanel; bridgeKey: string; route: string | null }) {
  const remote = useRemoteApp(bridgeKey);
  return (
    <PluginFrame
      {...frame}
      actions={remoteFrameActions(bridgeKey, remote.nav)}
      controls={<OpenInBrowser bridgeKey={bridgeKey} title={panel.title} />}
      icon={pluginIcon(panel.icon ?? 'app')}
      iconSrc={panel.iconSrc}
      layout="embedded"
      plugin={panel.plugin}
      title={remote.nav?.title || panel.title}
    >
      <RemoteAppPanel
        bridgeKey={bridgeKey}
        description={panel.description}
        iconSrc={panel.iconSrc}
        path={route}
        plugin={panel.remoteView?.plugin ?? panel.plugin}
        title={panel.title}
        visible
      />
    </PluginFrame>
  );
}

/** The item the remote app's tab opens (its deep link), set when it was docked on one. */
function useDockedPath(view: string): string | null {
  return useDesktopShellStore((state) => {
    const tab = state.panelTabs.tabs.find((candidate) => candidate.view === view);
    return tab === undefined ? null : (state.appPanelTargets[tab.id]?.route ?? null);
  });
}

/** The app docked in a conversation's side panel: its sections in a picker after the title. */
export function RemoteAppDocked({
  panel,
  visible,
  ...frame
}: RemoteFrameProps & { panel: PluginPanel; visible: boolean }) {
  const bridgeKey = dockedBridgeKey(panel.view);
  const remote = useRemoteApp(bridgeKey);
  const path = useDockedPath(panel.view);
  return (
    <PluginFrame
      {...frame}
      actions={remoteFrameActions(bridgeKey, remote.nav)}
      controls={<OpenInBrowser bridgeKey={bridgeKey} title={panel.title} />}
      icon={pluginIcon(panel.icon ?? 'app')}
      iconSrc={panel.iconSrc}
      layout="docked"
      picker={<RemoteSectionsPicker bridgeKey={bridgeKey} title={panel.title} />}
      plugin={panel.plugin}
      title={panel.title}
    >
      <RemoteAppPanel
        bridgeKey={bridgeKey}
        description={panel.description}
        iconSrc={panel.iconSrc}
        path={path}
        plugin={panel.remoteView?.plugin ?? panel.plugin}
        title={panel.title}
        visible={visible}
      />
    </PluginFrame>
  );
}

/** The sections the docked app sent, grouped, the shown one checked; a pick reaches the app. */
export function RemoteSectionsPicker({ bridgeKey, title }: { bridgeKey: string; title: string }) {
  const { nav } = useRemoteApp(bridgeKey);
  const groups = groupedSections(nav);
  if (groups.length === 0) return null;
  const label =
    nav?.sections.find((section) => section.active)?.label ?? (nav?.title || 'Sections');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={`${title} sections`}
          className="min-w-0 max-w-48 shrink"
          data-sections-picker
          size="sm"
          type="button"
          variant="ghost"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="size-3.5 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-96 min-w-56 overflow-y-auto">
        {groups.map(({ group, sections }, index) => (
          <Fragment key={group ?? ''}>
            {index > 0 && <DropdownMenuSeparator />}
            {group !== null && <DropdownMenuLabel>{group}</DropdownMenuLabel>}
            {sections.map((section) => {
              const Icon = section.icon === undefined ? null : pluginIcon(section.icon);
              return (
                <DropdownMenuItem
                  key={section.id}
                  onSelect={() =>
                    sendToRemoteApp(bridgeKey, { type: 'crow:navigate', section: section.id })
                  }
                >
                  {Icon === null ? (
                    <Check className={section.active ? 'size-4' : 'size-4 opacity-0'} />
                  ) : (
                    <Icon className="size-4" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{section.label}</span>
                  {section.count !== undefined && (
                    <span className="type-caption text-muted-foreground tabular-nums">
                      {section.count}
                    </span>
                  )}
                  {Icon !== null && section.active && <Check className="size-4" />}
                </DropdownMenuItem>
              );
            })}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Opens an item of a Crowlingo application (a CrowDrive file, a CrowBox thread: an app item ref
 * of a conversation) docked in the conversation on screen: the app's tab in the side panel, on
 * the item's deep link, shown embedded. False when the user has no remote app of that plugin.
 */
export function openRemoteAppItem(
  store: DesktopShellStore,
  panels: readonly Pick<PluginPanel, 'view' | 'plugin' | 'remoteView'>[],
  item: { plugin: string; url: string },
): boolean {
  const panel = panels.find(
    (candidate) =>
      candidate.remoteView !== undefined &&
      (candidate.remoteView.plugin === item.plugin || candidate.plugin === item.plugin),
  );
  const path = remoteItemPath(item.url);
  if (panel === undefined || path === null) return false;
  // Already docked on another item, the page goes there without loading again (`RemoteAppPanel`).
  dockApp(store, panel.view, { item: null, route: path });
  return true;
}
